import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { RegionOption } from './regions';

interface SearchableSelectProps {
  value: string;
  options: RegionOption[];
  placeholder?: string;
  label?: string;
  hasError?: boolean;
  clearable?: boolean;
  onChange: (value: string) => void;
}

export function SearchableSelect({
  value,
  options,
  placeholder = 'Select...',
  label,
  hasError = false,
  clearable = false,
  onChange,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().indexOf(q) !== -1);
  }, [options, search]);

  const displayText = useMemo(() => {
    const found = options.find((opt) => opt.value === value);
    return found ? found.label : '';
  }, [options, value]);

  // Click-outside handler
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  function toggle() {
    setIsOpen((prev) => {
      if (!prev) setSearch('');
      return !prev;
    });
  }

  function select(opt: RegionOption) {
    onChange(opt.value);
    setIsOpen(false);
    setSearch('');
  }

  function clear(e: React.SyntheticEvent) {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearch('');
  }

  return (
    <div className="kst-select-wrapper" ref={wrapperRef}>
      {label && <label className="kst-select-label">{label}</label>}
      <div
        className={`kst-select-trigger${hasError ? ' has-error' : ''}`}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        {displayText ? (
          <span>{displayText}</span>
        ) : (
          <span className="kst-select-placeholder">{placeholder}</span>
        )}
        <span className="kst-select-controls">
          {clearable && displayText && (
            <button
              type="button"
              className="kst-select-clear"
              aria-label="Clear selection"
              onClick={clear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  clear(e);
                }
              }}
            >
              &times;
            </button>
          )}
          <span className={`kst-select-arrow${isOpen ? ' open' : ''}`} />
        </span>
      </div>
      {isOpen && (
        <div className="kst-select-dropdown">
          <input
            ref={searchInputRef}
            className="kst-select-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            onClick={(e) => e.stopPropagation()}
          />
          {filteredOptions.length === 0 ? (
            <div className="kst-select-no-results">No results</div>
          ) : (
            filteredOptions.map((opt) => (
              <div
                key={opt.value}
                className={`kst-select-option${opt.value === value ? ' selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  select(opt);
                }}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
